import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, Building2, Stethoscope, Pill, TestTube, Truck, MapPin, Loader2, TrendingUp, GraduationCap, Award, Heart, Droplets, CalendarHeart, Siren } from "lucide-react";
import { Input } from "@/components/ui/input";
import { getApiUrl } from "@/lib/apiConfig";
import { motion, AnimatePresence } from "framer-motion";

interface Suggestion {
  id: number;
  name: string;
  category: string;
  categoryType: number;
  city?: string;
  state?: string;
  type: 'provider' | 'speciality' | 'degree';
}

interface SearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (suggestion: Suggestion) => void;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  // Sizing/text overrides only (default "h-14 text-base") — border/bg/rounded
  // stay fixed, same convention as CityAutocomplete's inputClassName.
  className?: string;
  // Real provider-type code (1 Hospital, 2 Doctor, 3 Pharmacy, 4 HomeCare+,
  // 6 Diagnostic, 7 Wholesaler, 8 Blood Bank, 9 Health Camp, 10 Ambulance).
  // When set, suggestions are scoped to this category only. When omitted,
  // all real categories are searched (used for an "All Categories" filter).
  category?: number;
}

// Every real, searchable provider-type code — 5 is a dead legacy duplicate
// of Ambulance (10) and is deliberately excluded.
const ALL_CATEGORIES = [1, 2, 3, 4, 6, 7, 8, 9, 10];

// Predefined specialities for doctors — kept broad on purpose: a patient searching
// "Cosmetologist" or "Dietician" got zero suggestions before because those simply
// weren't in this list, even though the backend (see getListings() in api.php) can
// find a real doctor with that specialization once the search term reaches it.
const SPECIALITIES = [
  "Oncologist",
  "Cardiologist",
  "Dermatologist",
  "Cosmetologist",
  "Neurologist",
  "Orthopedic Surgeon",
  "Pediatrician",
  "Gynecologist",
  "Ophthalmologist",
  "ENT Specialist",
  "Psychiatrist",
  "Psychologist",
  "Pulmonologist",
  "Gastroenterologist",
  "Hepatologist",
  "Urologist",
  "Andrologist",
  "Nephrologist",
  "Endocrinologist",
  "Diabetologist",
  "Rheumatologist",
  "Physiotherapist",
  "Dentist",
  "Orthodontist",
  "Homeopathy",
  "Ayurveda",
  "General Physician",
  "General Surgeon",
  "Radiologist",
  "Anesthesiologist",
  "Pathologist",
  "Plastic Surgeon",
  "Cosmetic Surgeon",
  "Bariatric Surgeon",
  "Neurosurgeon",
  "Cardiac Surgeon",
  "Vascular Surgeon",
  "Oncosurgeon",
  "Sexologist",
  "Dietician",
  "Nutritionist",
  "Sports Medicine",
  "Geriatrician",
  "Allergist",
  "Immunologist",
  "IVF Specialist",
  "Fertility Specialist",
  "Sonologist",
  "Podiatrist",
  "Speech Therapist",
  "Occupational Therapist",
  "Ayurvedic Physician",
];

// Predefined degrees/qualifications
const DEGREES = [
  "MBBS",
  "MD",
  "MS",
  "DNB",
  "DM",
  "MCh",
  "BDS",
  "MDS",
  "BAMS",
  "BHMS",
  "BUMS",
  "BPT",
  "MPT",
  "FRCS",
  "MRCP",
  "DCH",
  "DGO",
  "DA",
  "PhD",
];

const getCategoryIcon = (categoryType: number, type?: string) => {
  if (type === 'speciality') return <Stethoscope className="w-4 h-4" />;
  if (type === 'degree') return <GraduationCap className="w-4 h-4" />;
  
  switch (categoryType) {
    case 1: return <Building2 className="w-4 h-4" />;
    case 2: return <Stethoscope className="w-4 h-4" />;
    case 3: return <Pill className="w-4 h-4" />;
    case 4: return <Heart className="w-4 h-4" />;
    case 6: return <TestTube className="w-4 h-4" />;
    case 7: return <Truck className="w-4 h-4" />;
    case 8: return <Droplets className="w-4 h-4" />;
    case 9: return <CalendarHeart className="w-4 h-4" />;
    case 10: return <Siren className="w-4 h-4" />;
    default: return <Building2 className="w-4 h-4" />;
  }
};

const getCategoryColor = (categoryType: number, type?: string) => {
  if (type === 'speciality') return "from-violet-500 to-purple-600";
  if (type === 'degree') return "from-amber-500 to-orange-500";
  
  switch (categoryType) {
    case 1: return "from-blue-500 to-blue-600";
    case 2: return "from-emerald-500 to-teal-500";
    case 3: return "from-purple-500 to-pink-500";
    case 4: return "from-rose-500 to-pink-500";
    case 6: return "from-orange-500 to-amber-500";
    case 7: return "from-cyan-500 to-blue-500";
    case 8: return "from-red-500 to-rose-600";
    case 9: return "from-teal-500 to-emerald-600";
    case 10: return "from-red-600 to-orange-500";
    default: return "from-primary to-cyan-500";
  }
};

const getCategoryLabel = (categoryType: number) => {
  switch (categoryType) {
    case 1: return "Hospital";
    case 2: return "Doctor";
    case 3: return "Pharmacy";
    case 4: return "HomeCare+";
    case 6: return "Diagnostic";
    case 7: return "Wholesaler";
    case 8: return "Blood Bank";
    case 9: return "Health Camp";
    case 10: return "Ambulance";
    default: return "Healthcare";
  }
};

const SearchAutocomplete = ({
  value,
  onChange,
  onSelect,
  onKeyPress,
  placeholder = "Search...",
  className = "h-14 text-base",
  category,
}: SearchAutocompleteProps) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const updateDropdownPosition = () => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;

    const width = rect.width;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const top = rect.bottom + 12;

    setDropdownPos({ top, left, width });
  };

  // Get matching specialities and degrees based on query. Specialities/degrees
  // only ever apply to Doctors (category 2) — scoped out entirely for any other
  // explicitly-selected category, and still offered when no category is set
  // (an "All Categories" search).
  const getLocalMatches = (query: string): Suggestion[] => {
    if (query.length < 2) return [];
    if (category !== undefined && category !== 2) return [];

    const lowerQuery = query.toLowerCase();
    const results: Suggestion[] = [];
    
    // Match specialities
    const matchedSpecialities = SPECIALITIES
      .filter(s => s.toLowerCase().includes(lowerQuery))
      .slice(0, 3);
    
    matchedSpecialities.forEach((spec, index) => {
      results.push({
        id: -(index + 1), // Negative ID for local suggestions
        name: spec,
        category: "Speciality",
        categoryType: 2, // Doctor category
        type: 'speciality',
      });
    });
    
    // Match degrees
    const matchedDegrees = DEGREES
      .filter(d => d.toLowerCase().includes(lowerQuery))
      .slice(0, 2);
    
    matchedDegrees.forEach((deg, index) => {
      results.push({
        id: -(100 + index), // Different negative ID range
        name: deg,
        category: "Qualification",
        categoryType: 2,
        type: 'degree',
      });
    });
    
    return results;
  };

  // Fetch suggestions from API
  const fetchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const apiUrl = getApiUrl();
      
      // Get local matches first (specialities and degrees) and show them
      // immediately — the "All Categories" fallback below can sequentially
      // query up to 9 categories (measured ~1.5-2s round-trip on real data),
      // and specialities/degrees don't need to wait on that at all since
      // they're computed locally with no network call.
      const localMatches = getLocalMatches(query);
      if (localMatches.length > 0) {
        setSuggestions(localMatches);
      }

      // Always scope to the real, currently-active category when one is
      // passed in (e.g. the homepage's active tab) — previously this guessed
      // the category from whether the typed text loosely matched the
      // speciality/degree word lists, which meant almost any query (even on
      // the Hospital/Pharmacy/Ambulance/etc. tabs) got silently redirected to
      // doctors-only results. When no category is passed (an "All
      // Categories" search), query every real category.
      const categories = category !== undefined ? [category] : ALL_CATEGORIES;
      
      // Sequential calls to avoid MySQL max_user_connections limit
      const providerSuggestions: Suggestion[] = [];
      for (const cat of categories) {
        try {
          const res = await fetch(`${apiUrl}?action=listings&type=${cat}&search=${encodeURIComponent(query)}&limit=2`);
          const result = await res.json();
          if (result.data && Array.isArray(result.data)) {
            result.data.slice(0, 2).forEach((item: any) => {
              const name = item.name || item.hospital_name || item.doctor_name || item.store_name;
              if (name) {
                providerSuggestions.push({
                  id: item.id,
                  name: name,
                  category: getCategoryLabel(cat),
                  categoryType: cat,
                  city: item.city,
                  state: item.state,
                  type: 'provider',
                });
              }
            });
          }
        } catch {
          // skip failed category
        }
      }
      // Sort providers by relevance (name starts with query first)
      const sortedProviders = providerSuggestions
        .sort((a, b) => {
          const aStartsWith = a.name.toLowerCase().startsWith(query.toLowerCase());
          const bStartsWith = b.name.toLowerCase().startsWith(query.toLowerCase());
          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;
          return 0;
        })
        .slice(0, 4);

      // Combine: local matches first, then providers
      const allSuggestions = [...localMatches, ...sortedProviders].slice(0, 8);
      
      setSuggestions(allSuggestions);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      // Still show local matches if API fails
      setSuggestions(getLocalMatches(query));
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 500); // Increased debounce to reduce API calls

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, category]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInInput = !!wrapperRef.current && wrapperRef.current.contains(target);
      const clickedInDropdown = !!dropdownRef.current && dropdownRef.current.contains(target);
      if (!clickedInInput && !clickedInDropdown) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keep dropdown above hero/stats (avoid stacking-context/z-index issues)
  useEffect(() => {
    if (!showSuggestions) {
      setDropdownPos(null);
      return;
    }

    updateDropdownPosition();
    const handler = () => updateDropdownPosition();

    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [showSuggestions, value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setShowSuggestions(true);
    setSelectedIndex(-1);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    onChange(suggestion.name);
    setShowSuggestions(false);
    onSelect?.(suggestion);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      onKeyPress?.(e);
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          e.preventDefault();
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          onKeyPress?.(e);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        onKeyPress?.(e);
    }
  };

  // Group suggestions by type for display
  const specialitySuggestions = suggestions.filter(s => s.type === 'speciality');
  const degreeSuggestions = suggestions.filter(s => s.type === 'degree');
  const providerSuggestions = suggestions.filter(s => s.type === 'provider');

  return (
    <div ref={wrapperRef} className="relative flex-1 group">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors z-10" />
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => value.length >= 2 && setShowSuggestions(true)}
        data-testid="search-input"
        className={`pl-12 border-2 border-border focus:border-primary rounded-xl bg-white transition-all ${className}`}
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </div>
      )}

      {/* Suggestions dropdown (portaled so it never goes behind hero/stats) */}
      {typeof document !== "undefined" && dropdownPos &&
        createPortal(
          <AnimatePresence>
            {showSuggestions && suggestions.length > 0 && (
              <motion.div
                ref={dropdownRef}
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="fixed rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-gray-100 overflow-hidden z-[9999]"
                style={{
                  top: dropdownPos.top,
                  left: dropdownPos.left,
                  width: dropdownPos.width,
                  backgroundColor: "#ffffff",
                }}
              >
                {/* Specialities Section */}
                {specialitySuggestions.length > 0 && (
                  <>
                    <div className="px-4 py-2 border-b border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
                      <div className="flex items-center gap-2 text-xs font-medium text-violet-600">
                        <Stethoscope className="w-3.5 h-3.5" />
                        <span>Specialities</span>
                      </div>
                    </div>
                    <div className="py-1">
                      {specialitySuggestions.map((suggestion, index) => {
                        const globalIndex = suggestions.indexOf(suggestion);
                        return (
                          <motion.button
                            key={`spec-${suggestion.name}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-200 ${
                              selectedIndex === globalIndex 
                                ? "bg-violet-50 border-l-2 border-l-violet-500" 
                                : "hover:bg-gray-50 border-l-2 border-l-transparent"
                            }`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 text-white shadow-sm">
                              <Stethoscope className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 text-sm">{suggestion.name}</div>
                              <div className="text-[11px] text-violet-600">Find {suggestion.name} doctors</div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Degrees Section */}
                {degreeSuggestions.length > 0 && (
                  <>
                    <div className="px-4 py-2 border-b border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
                      <div className="flex items-center gap-2 text-xs font-medium text-amber-600">
                        <GraduationCap className="w-3.5 h-3.5" />
                        <span>Qualifications</span>
                      </div>
                    </div>
                    <div className="py-1">
                      {degreeSuggestions.map((suggestion, index) => {
                        const globalIndex = suggestions.indexOf(suggestion);
                        return (
                          <motion.button
                            key={`deg-${suggestion.name}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-200 ${
                              selectedIndex === globalIndex 
                                ? "bg-amber-50 border-l-2 border-l-amber-500" 
                                : "hover:bg-gray-50 border-l-2 border-l-transparent"
                            }`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 text-white shadow-sm">
                              <GraduationCap className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 text-sm">{suggestion.name}</div>
                              <div className="text-[11px] text-amber-600">Doctors with {suggestion.name} degree</div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Providers Section */}
                {providerSuggestions.length > 0 && (
                  <>
                    <div className="px-4 py-2 border-b border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                        <TrendingUp className="w-3.5 h-3.5" />
                        <span>Healthcare Providers</span>
                      </div>
                    </div>
                    <div className="py-1">
                      {providerSuggestions.map((suggestion, index) => {
                        const globalIndex = suggestions.indexOf(suggestion);
                        return (
                          <motion.button
                            key={`${suggestion.categoryType}-${suggestion.id}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all duration-200 ${
                              selectedIndex === globalIndex 
                                ? "bg-primary/5 border-l-2 border-l-primary" 
                                : "hover:bg-gray-50 border-l-2 border-l-transparent"
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getCategoryColor(suggestion.categoryType)} flex items-center justify-center flex-shrink-0 text-white shadow-sm`}>
                              {getCategoryIcon(suggestion.categoryType)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate text-sm">
                                {suggestion.name}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gradient-to-r ${getCategoryColor(suggestion.categoryType)} text-white`}>
                                  {suggestion.category}
                                </span>
                                {suggestion.city && (
                                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                    <MapPin className="w-3 h-3" />
                                    {suggestion.city}
                                  </span>
                                )}
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </>
                )}
                
                {/* Footer */}
                <div className="px-4 py-2.5 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100">
                  <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono shadow-sm">↑</kbd>
                      <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono shadow-sm">↓</kbd>
                      <span className="ml-1">Navigate</span>
                    </span>
                    <span className="w-px h-3 bg-gray-300" />
                    <span className="flex items-center gap-1">
                      <kbd className="px-2 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono shadow-sm">Enter</kbd>
                      <span className="ml-1">Select</span>
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

      {/* No results message (also portaled) */}
      {typeof document !== "undefined" && dropdownPos &&
        createPortal(
          <AnimatePresence>
            {showSuggestions && value.length >= 2 && !isLoading && suggestions.length === 0 && (
              <motion.div
                ref={dropdownRef}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="fixed rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.2)] border border-gray-100 overflow-hidden z-[9999] p-6 text-center"
                style={{
                  top: dropdownPos.top,
                  left: dropdownPos.left,
                  width: dropdownPos.width,
                  backgroundColor: "#ffffff",
                }}
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                  <Search className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-600">No results found</p>
                <p className="text-xs text-muted-foreground mt-1">Try searching with different keywords</p>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default SearchAutocomplete;
